<template>
  <div class="resource-detail">
    <AppHeader />

    <ResourceDetailSkeleton v-if="loading" />

    <template v-else-if="detail">
      <nav class="resource-detail__breadcrumb">
        <!-- <div class="resource-detail__breadcrumb-inner">
          <router-link to="/resources">馆藏资源</router-link>
          <span>/</span>
          <span>{{ detail.resourceTypeLabel || '资源' }}</span>
          <span>/</span>
          <span class="resource-detail__breadcrumb-current">资源详情</span>
        </div> -->
      </nav>

      <div class="resource-detail__body">
        <section class="resource-detail__card">
          <div class="resource-detail__hero">
            <div class="resource-detail__cover">
              <img
                v-if="detail.coverImage"
                :src="detail.coverImage"
                :alt="detail.title"
              />
              <div v-else class="resource-detail__cover-placeholder">
                <span class="resource-detail__cover-icon">📖</span>
                <span>暂无封面</span>
              </div>
            </div>

            <div class="resource-detail__info">
              <h1 class="resource-detail__title">{{ detail.title }}</h1>

              <div class="resource-detail__meta-grid">
                <div
                  v-for="(column, columnIndex) in metaColumns"
                  :key="columnIndex"
                  class="resource-detail__meta-col"
                >
                  <p v-for="field in column" :key="field.id || field.label">
                    <span>{{ field.label }}：</span>{{ field.value || '-' }}
                  </p>
                </div>
              </div>

              <div class="resource-detail__abstract">
                <h2>资源摘要</h2>
                <p>{{ detail.abstract || '暂无摘要' }}</p>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="!(detail.websites && detail.websites.length)"
          class="resource-detail__files"
        >
          <div class="resource-detail__files-header">
            <h2>文件列表</h2>
            <span>点击「预览」将在新页面打开文件</span>
          </div>

          <div class="resource-detail__files-summary">共 {{ detail.fileCount }} 个文件</div>

          <div class="resource-detail__table">
            <div class="resource-detail__table-head">
              <span>文件名</span>
              <span>类型</span>
              <span>大小</span>
              <span>操作</span>
            </div>

            <div
              v-for="file in detail.files"
              :key="file.id"
              class="resource-detail__table-row"
            >
              <div class="resource-detail__file-name">
                <i
                  class="resource-detail__file-icon"
                  :class="fileIconClasses(file)"
                />
                <span>{{ file.name }}</span>
              </div>
              <span>{{ file.type }}</span>
              <span>{{ file.size }}</span>
              <button
                type="button"
                class="resource-detail__preview-btn"
                :disabled="!file.previewUrl"
                @click="previewFile(file)"
              >
                预览
              </button>
            </div>

            <div v-if="!detail.files.length" class="resource-detail__files-empty">
              暂无文件
            </div>
          </div>
        </section>

        <section
          v-if="detail.websites && detail.websites.length"
          class="resource-detail__files resource-detail__websites"
        >
          <div class="resource-detail__files-header">
            <h2>参考网站</h2>
            <span>点击链接或「预览」将在新标签打开网站</span>
          </div>

          <div class="resource-detail__files-summary">共 {{ detail.websites.length }} 个参考网站</div>

          <div class="resource-detail__table resource-detail__table--website">
            <div class="resource-detail__table-head">
              <span>网站名称</span>
              <span>网站链接</span>
              <span>操作</span>
            </div>

            <div
              v-for="site in detail.websites"
              :key="'site-' + site.id"
              class="resource-detail__table-row"
            >
              <div class="resource-detail__file-name">
                <span>{{ site.name }}</span>
              </div>
              <a
                class="resource-detail__site-link"
                :href="site.website"
                target="_blank"
                rel="noopener noreferrer"
                :title="site.website"
              >
                <i class="el-icon-link resource-detail__site-link-icon" />
                <span>{{ websiteHost(site.website) }}</span>
              </a>
              <button
                type="button"
                class="resource-detail__preview-btn"
                :disabled="!site.website"
                @click="openWebsite(site)"
              >
                预览
              </button>
            </div>
          </div>
        </section>
      </div>
    </template>

    <div v-else-if="accessBlocked" class="resource-detail__empty">
      <p>档案类资源需登录后访问</p>
      <button type="button" class="resource-detail__back" @click="loginVisible = true">去登录</button>
    </div>

    <div v-else class="resource-detail__empty">
      <p>未找到该资源</p>
      <router-link to="/resources" class="resource-detail__back">返回馆藏资源</router-link>
    </div>

    <AppFooter />

    <LoginDialog v-model="loginVisible" />
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import LoginDialog from '@/components/common/LoginDialog.vue'
import ResourceDetailSkeleton from './components/ResourceDetailSkeleton.vue'
import { fetchResourceDetail, mapResourceDetail } from '@/api/resources'
import { getFileIconMeta, isPdfFile } from '@/utils/fileIcon'
import { recordBrowseHistory, LOG_HISTORY_TYPE } from '@/utils/browseHistory'
import {
  isArchiveResource,
  getArchivePdfPreviewBlockReason,
  getArchivePdfPreviewBlockMessage
} from '@/utils/resourceAccess'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'ResourceDetail',
  components: {
    AppHeader,
    AppFooter,
    LoginDialog,
    ResourceDetailSkeleton
  },
  data() {
    return {
      detail: null,
      loading: false,
      loginVisible: false,
      accessBlocked: false
    }
  },
  computed: {
    ...mapState('user', ['userInfo']),
    ...mapGetters(['isLoggedIn']),
    metaColumns() {
      if (!this.detail) {
        return [[], [], []]
      }
      const fields = [
        { id: 'resource-type', label: '资源类型', value: this.detail.resourceTypeLabel },
        { id: 'collection-no', label: '馆藏编号', value: this.detail.collectionNo },
        { id: 'file-count', label: '文件数量', value: String(this.detail.fileCount) },
        ...this.detail.metaAttributes
      ]
      const columns = [[], [], []]
      fields.forEach((field, index) => {
        columns[index % 3].push(field)
      })
      return columns
    }
  },
  watch: {
    '$route.params.id'() {
      this.loadDetail()
    },
    isLoggedIn(loggedIn) {
      if (loggedIn && this.accessBlocked) {
        this.loadDetail()
      }
    }
  },
  mounted() {
    document.documentElement.classList.add('hide-scrollbar')
    this.loadDetail()
  },
  beforeDestroy() {
    document.documentElement.classList.remove('hide-scrollbar')
  },
  methods: {
    async loadDetail() {
      const id = this.$route.params.id
      if (!id) {
        this.detail = null
        return
      }
      this.loading = true
      this.accessBlocked = false
      try {
        const data = await fetchResourceDetail(id)
        const mapped = mapResourceDetail(data)
        if (isArchiveResource(mapped) && !this.isLoggedIn) {
          this.detail = null
          this.accessBlocked = true
          this.loginVisible = true
          return
        }
        this.detail = mapped
        recordBrowseHistory(LOG_HISTORY_TYPE.RESOURCE, id)
      } catch (error) {
        this.detail = null
      } finally {
        this.loading = false
      }
    },
    previewFile(file) {
      if (!file.previewUrl) return
      if (isPdfFile(file)) {
        const blockReason = getArchivePdfPreviewBlockReason(
          this.userInfo,
          this.isLoggedIn,
          this.detail
        )
        if (blockReason) {
          this.showArchivePreviewBlocked(blockReason)
          return
        }
        const route = this.$router.resolve({
          path: '/resources/preview',
          query: {
            url: file.previewUrl,
            name: file.name,
            resourceType: this.detail.resourceType || '',
            resourceTypeLabel: this.detail.resourceTypeLabel || ''
          }
        })
        window.open(route.href, '_blank')
        return
      }
      window.open(file.previewUrl, '_blank')
    },
    openWebsite(site) {
      const url = site && (site.website || site.previewUrl)
      if (!url) return
      window.open(url, '_blank')
    },
    websiteHost(url) {
      if (!url) return '-'
      try {
        const host = new URL(url).hostname.replace(/^www\./i, '')
        return host || url
      } catch (e) {
        return String(url)
          .replace(/^https?:\/\//i, '')
          .replace(/^www\./i, '')
          .split('/')[0] || url
      }
    },
    showArchivePreviewBlocked(reason) {
      const message = getArchivePdfPreviewBlockMessage(reason)
      if (reason === 'needLogin') {
        this.$confirm(message, '提示', {
          confirmButtonText: '去登录',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.loginVisible = true
        }).catch(() => {})
        return
      }
      this.$alert(message, '提示', {
        confirmButtonText: '我知道了',
        type: 'warning'
      })
    },
    fileIconClasses(file) {
      const { iconClass, kind } = getFileIconMeta(file)
      return [iconClass, `resource-detail__file-icon--${kind}`]
    }
  }
}
</script>

<style lang="scss" scoped>
.resource-detail {
  min-height: 100vh;
  background:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"),
    $color-paper;

  &__empty {
    padding: $spacing-4xl;
    text-align: center;
    color: $color-text-light;
  }

  &__back {
    display: inline-block;
    margin-top: $spacing-base;
    color: $color-green-dark;
  }

  &__breadcrumb {
    height: 80px;
  }

  &__breadcrumb-inner {
    max-width: $container-max-width;
    margin: 0 auto;
  }

  &__breadcrumb-current {
    color: $color-brown-dark;
  }

  &__body {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: $spacing-xl $content-padding $spacing-4xl;
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
  }

  &__card,
  &__files {
    background: $color-white;
    border-radius: 8px;
    box-shadow: 0 2px 16px rgba($color-brown-dark, 0.06);
  }

  &__card {
    padding: $spacing-xl;
  }

  &__hero {
    display: flex;
    gap: $spacing-2xl;
    align-items: flex-start;
  }

  &__cover {
    width: 220px;
    flex-shrink: 0;
    border-radius: 6px;
    overflow: hidden;
    background: $color-paper-light;
    box-shadow: 0 4px 16px rgba($color-brown-dark, 0.12);

    img {
      display: block;
      width: 100%;
      height: 300px;
      object-fit: cover;
    }
  }

  &__cover-placeholder {
    height: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: $spacing-sm;
    color: $color-text-light;
    background:
      linear-gradient(145deg, rgba($color-cream, 0.95), rgba($color-paper-light, 0.9));

    span:last-child {
      font-size: rem(13);
    }
  }

  &__cover-icon {
    font-size: rem(42);
    opacity: 0.45;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: $font-serif;
    font-size: rem(28);
    font-weight: 700;
    color: $color-brown-dark;
    margin-bottom: $spacing-lg;
    line-height: 1.4;
  }

  &__meta-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: $spacing-lg $spacing-xl;
    margin-bottom: $spacing-xl;
  }

  &__meta-col p {
    font-size: rem(13);
    line-height: 2;
    color: $color-text;

    span {
      color: $color-text-light;
    }
  }

  &__abstract {
    padding-top: $spacing-lg;
    border-top: 1px solid rgba($color-border, 0.35);

    h2 {
      font-size: rem(15);
      font-weight: 700;
      color: $color-brown-dark;
      margin-bottom: $spacing-sm;
    }

    p {
      font-size: rem(14);
      line-height: 1.9;
      color: $color-text-light;
    }
  }

  &__files {
    padding: $spacing-xl;
  }

  &__files-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-base;
    margin-bottom: $spacing-base;

    h2 {
      font-size: rem(18);
      font-weight: 700;
      color: $color-brown-dark;
    }

    span {
      font-size: rem(12);
      color: $color-text-light;
    }
  }

  &__files-summary {
    padding: $spacing-sm $spacing-base;
    background: rgba($color-cream, 0.8);
    border-radius: 4px;
    font-size: rem(13);
    color: $color-text;
    margin-bottom: $spacing-sm;
  }

  &__table-head,
  &__table-row {
    display: grid;
    grid-template-columns: minmax(0, 1.8fr) 100px 100px 100px;
    gap: $spacing-base;
    align-items: center;
    padding: $spacing-md $spacing-base;
  }

  &__table--website {
    .resource-detail__table-head,
    .resource-detail__table-row {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr) 100px;
    }
  }

  &__site-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
    color: $color-green-dark;
    text-decoration: none;

    span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &__site-link-icon {
    flex-shrink: 0;
    font-size: rem(14);
  }

  &__table-head {
    font-size: rem(13);
    font-weight: 600;
    color: $color-text-light;
    border-bottom: 1px solid rgba($color-border, 0.35);
  }

  &__table-row {
    font-size: rem(13);
    color: $color-text;
    border-bottom: 1px solid rgba($color-border, 0.2);

    &:last-child {
      border-bottom: none;
    }
  }

  &__file-name {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    min-width: 0;

    span:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__file-icon {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: rem(16);
    line-height: 1;

    &--image {
      background: rgba($color-green-light, 0.18);
      color: $color-green-dark;
    }

    &--pdf {
      background: rgba(#c45c5c, 0.15);
      color: #c45c5c;
    }

    &--zip {
      background: rgba(#6b8cae, 0.15);
      color: #4a6f8f;
    }

    &--video {
      background: rgba(#8b6cae, 0.15);
      color: #6b4f8f;
    }

    &--audio {
      background: rgba($warning-color, 0.2);
      color: #9a7340;
    }

    &--doc {
      background: rgba(#4a7fc4, 0.15);
      color: #3a6aa8;
    }

    &--excel {
      background: rgba($color-green-light, 0.2);
      color: $color-green-dark;
    }

    &--file {
      background: rgba($color-text-light, 0.12);
      color: $color-text-light;
    }
  }

  &__preview-btn {
    justify-self: start;
    padding: 6px 18px;
    border: none;
    border-radius: 4px;
    background: $color-green-dark;
    color: $color-white;
    font-size: rem(13);
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
      opacity: 0.88;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__files-empty {
    padding: $spacing-2xl;
    text-align: center;
    color: $color-text-light;
    font-size: rem(13);
  }
}

@media (max-width: 960px) {
  .resource-detail {
    &__hero {
      flex-direction: column;
    }

    &__cover {
      width: 100%;
      max-width: 280px;
      margin: 0 auto;
    }

    &__meta-grid {
      grid-template-columns: 1fr;
    }

    &__files-header {
      flex-direction: column;
      align-items: flex-start;
    }

    &__table-head {
      display: none;
    }

    &__table-row {
      grid-template-columns: 1fr;
      gap: $spacing-sm;
      padding: $spacing-base 0;
    }
  }
}
</style>
